import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Radio, RefreshCw, Settings2, ChevronLeft, ChevronRight, Megaphone, CalendarDays, Handshake,
  AtSign, Smile, Plus, MessageCircle, X, Search, Loader2, Users, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatRelative } from "../../lib/constants";

const MOOD = {
  buzzing: "from-orange-50 border-orange-200",
  positive: "from-emerald-50 border-emerald-200",
  neutral: "from-slate-50 border-slate-200",
  tense: "from-rose-50 border-rose-200",
  quiet: "from-slate-50 border-slate-200",
};

async function createTask(title, notes, category) {
  try {
    await api.post("/tasks/create", { title, notes, category, priority: "medium" });
    toast.success("Task created", { description: title });
    window.dispatchEvent(new CustomEvent("brainydo:tasks-changed"));
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Couldn't create task");
  }
}

function PulseCard({ c, i }) {
  const chatLink = `/app/messages?chat=${encodeURIComponent(c.chat_id)}`;
  const wantsYou = (c.asks_for_you || []).length > 0 || c.mention_count > 0;
  return (
    <article className={`snap-start shrink-0 w-[300px] sm:w-[340px] rounded-xl border bg-gradient-to-b to-white p-4 flex flex-col sw-fade-up ${MOOD[c.mood] || MOOD.neutral}`}
      style={{ animationDelay: `${i * 60}ms` }} data-testid={`pulse-card-${c.chat_id}`}>
      <div className="flex items-start gap-2">
        <span className="text-[26px] leading-none" title={c.mood}>{c.mood_emoji}</span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-bold text-slate-900 truncate">{c.chat_name}</div>
          <div className="text-[11px] text-slate-500">{c.message_count} msgs · {c.people_count} people · {formatRelative(c.created_at)}</div>
        </div>
        {wantsYou && (
          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5 sw-pop">
            <AtSign size={10} /> You
          </span>
        )}
      </div>
      {c.headline && <p className="text-[13px] font-semibold text-slate-800 mt-2.5 leading-snug">{c.headline}</p>}

      <div className="mt-3 space-y-3 text-[12.5px] text-slate-700 flex-1">
        {(c.asks_for_you || []).length > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-800 mb-1 flex items-center gap-1"><AtSign size={11} /> Needs you</div>
            {c.asks_for_you.map((a, k) => (
              <div key={k} className="flex items-start gap-2 py-0.5">
                <span className="flex-1"><b className="font-semibold">{a.who}</b>: {a.text}</span>
                <button onClick={() => createTask(`${a.who}: ${a.text}`.slice(0, 120), `From group "${c.chat_name}".`, "follow_up")}
                  className="shrink-0 h-6 px-1.5 rounded border border-amber-300 bg-white text-amber-800 text-[10.5px] font-semibold hover:bg-amber-100 inline-flex items-center gap-0.5" title="Create a task">
                  <Plus size={10} /> Task
                </button>
              </div>
            ))}
          </div>
        )}

        {(c.topics || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {c.topics.map((t, k) => (
              <span key={k} title={t.detail} className="px-2 py-0.5 rounded-full bg-white border border-slate-200 text-[11.5px] font-medium text-slate-700 cursor-help">{t.title}</span>
            ))}
          </div>
        )}

        {(c.announcements || []).length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-1"><Megaphone size={11} /> Announcements</div>
            <ul className="space-y-0.5">{c.announcements.map((a, k) => <li key={k} className="leading-snug">• {a}</li>)}</ul>
          </div>
        )}

        {(c.events || []).length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1 flex items-center gap-1"><CalendarDays size={11} /> Events</div>
            {c.events.map((e, k) => (
              <div key={k} className="leading-snug">📅 <b className="font-semibold">{e.title}</b>{e.when ? <span className="text-slate-500"> · {e.when}</span> : null}</div>
            ))}
          </div>
        )}

        {(c.leads || []).length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700 mb-1 flex items-center gap-1"><Handshake size={11} /> Leads & asks</div>
            {c.leads.map((l, k) => (
              <div key={k} className="flex items-start gap-2 py-0.5">
                <span className="flex-1 leading-snug"><b className="font-semibold">{l.who}</b> — {l.need}</span>
                <button onClick={() => createTask(`${l.who}: ${l.need}`.slice(0, 120), `Lead spotted in group "${c.chat_name}".`, "opportunity")}
                  className="shrink-0 h-6 px-1.5 rounded border border-emerald-300 bg-white text-emerald-700 text-[10.5px] font-semibold hover:bg-emerald-50 inline-flex items-center gap-0.5" title="Track as an opportunity">
                  <Plus size={10} /> Lead
                </button>
              </div>
            ))}
          </div>
        )}

        {c.fun_moment && (
          <div className="rounded-lg bg-white/80 border border-slate-100 p-2 italic text-slate-600 flex gap-1.5">
            <Smile size={14} className="shrink-0 mt-0.5 text-pink-500" /> {c.fun_moment}
          </div>
        )}
      </div>

      <Link to={chatLink} className="mt-3 h-8 rounded-lg border border-slate-200 bg-white text-[12px] font-semibold text-slate-700 hover:border-slate-400 inline-flex items-center justify-center gap-1.5">
        <MessageCircle size={13} /> Open group
      </Link>
    </article>
  );
}

function ManageGroups({ onClose }) {
  const [groups, setGroups] = useState(null);
  const [max, setMax] = useState(15);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/dashboard/pulse/groups").then((r) => { setGroups(r.data.groups || []); setMax(r.data.max_groups || 15); })
      .catch(() => { toast.error("Couldn't load groups"); setGroups([]); });
  }, []);

  const count = (groups || []).filter((g) => g.enabled).length;
  const shown = useMemo(() => (groups || []).filter((g) => g.name.toLowerCase().includes(q.toLowerCase())), [groups, q]);

  const toggle = async (g) => {
    setBusy(g.chat_id);
    try {
      await api.put("/dashboard/pulse/groups", { chat_id: g.chat_id, enabled: !g.enabled });
      setGroups((cur) => cur.map((x) => (x.chat_id === g.chat_id ? { ...x, enabled: !x.enabled } : x)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-[1px] grid place-items-center p-4 sw-fade-in" onMouseDown={onClose}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden sw-pop" onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Radio size={16} className="text-orange-500" />
          <div className="flex-1">
            <div className="text-[15px] font-bold text-slate-900">Choose groups to follow</div>
            <div className="text-[12px] text-slate-500">Brainydo summarises them every 2 hours. {count}/{max} selected.</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="px-5 pt-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a group…"
              className="w-full h-9 pl-8 pr-3 border border-slate-200 rounded-lg text-[13px] focus:border-blue-500 outline-none" />
          </div>
        </div>
        <div className="max-h-[55vh] overflow-y-auto px-2 py-2">
          {groups === null && <div className="py-8 text-center text-slate-400 text-[13px]"><Loader2 size={16} className="inline animate-spin" /></div>}
          {groups && !shown.length && <div className="py-8 text-center text-slate-400 text-[13px]">No groups with recent messages.</div>}
          {shown.map((g) => (
            <button key={g.chat_id} onClick={() => toggle(g)} disabled={busy === g.chat_id || (!g.enabled && count >= max)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 text-left disabled:opacity-50">
              <span className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center shrink-0"><Users size={14} className="text-slate-500" /></span>
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-semibold text-slate-900 truncate">{g.name}</span>
                <span className="block text-[11px] text-slate-500">{g.messages} recent messages</span>
              </span>
              <span className={"w-10 h-6 rounded-full p-0.5 transition-colors " + (g.enabled ? "bg-emerald-500" : "bg-slate-200")}>
                <span className={"block w-5 h-5 rounded-full bg-white shadow transition-transform grid place-items-center " + (g.enabled ? "translate-x-4" : "")}>
                  {busy === g.chat_id ? <Loader2 size={11} className="animate-spin text-slate-400" /> : g.enabled ? <Check size={11} className="text-emerald-600" /> : null}
                </span>
              </span>
            </button>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/70 text-[11.5px] text-slate-500">
          Highlights skip personal details and only cover chats Brainydo already reads. Off by default for every group.
        </div>
      </div>
    </div>
  );
}

// "What's buzzing" — AI highlight cards for WhatsApp groups the user follows.
export default function GroupPulse() {
  const [data, setData] = useState(null);
  const [manage, setManage] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const scroller = useRef(null);

  const load = () => api.get("/dashboard/pulse").then((r) => setData(r.data)).catch(() => setData({ cards: [], enabled_count: 0 }));
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/dashboard/pulse/refresh");
      await load();
      toast.success(r.data.built ? `Updated ${r.data.built} group${r.data.built === 1 ? "" : "s"}` : "No new group chatter since the last update");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const scrollBy = (d) => scroller.current?.scrollBy({ left: d * 360, behavior: "smooth" });

  if (!data) return null;
  const cards = data.cards || [];

  return (
    <section className="bg-white rounded-xl border border-slate-200" data-testid="group-pulse">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 rounded-t-xl bg-gradient-to-r from-orange-50/60 to-white">
        <span className="relative flex w-2 h-2">
          {cards.length > 0 && <span className="absolute inset-0 rounded-full bg-orange-400 animate-ping" />}
          <span className="relative w-2 h-2 rounded-full bg-orange-500" />
        </span>
        <h3 className="text-[11px] font-bold uppercase tracking-[0.16em] text-orange-700">What's buzzing in your groups</h3>
        <div className="ml-auto flex items-center gap-1">
          {cards.length > 1 && (
            <>
              <button onClick={() => scrollBy(-1)} className="hidden md:grid h-7 w-7 place-items-center rounded-lg hover:bg-slate-100 text-slate-500" aria-label="Previous"><ChevronLeft size={15} /></button>
              <button onClick={() => scrollBy(1)} className="hidden md:grid h-7 w-7 place-items-center rounded-lg hover:bg-slate-100 text-slate-500" aria-label="Next"><ChevronRight size={15} /></button>
            </>
          )}
          {data.enabled_count > 0 && (
            <button onClick={refresh} disabled={refreshing} className="h-7 px-2 rounded-lg hover:bg-slate-100 text-slate-500 text-[12px] inline-flex items-center gap-1 disabled:opacity-50" title="Refresh now">
              <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> <span className="hidden sm:inline">Refresh</span>
            </button>
          )}
          <button onClick={() => setManage(true)} className="h-7 px-2 rounded-lg hover:bg-slate-100 text-slate-500 text-[12px] inline-flex items-center gap-1" data-testid="pulse-manage">
            <Settings2 size={13} /> <span className="hidden sm:inline">Groups</span>
          </button>
        </div>
      </div>

      {data.enabled_count === 0 ? (
        <div className="px-6 py-8 flex flex-col md:flex-row items-center gap-5">
          <div className="text-[44px] leading-none sw-wave origin-bottom">📣</div>
          <div className="flex-1 text-center md:text-left">
            <div className="text-[15px] font-bold text-slate-900">Never scroll 300 group messages again</div>
            <p className="text-[13px] text-slate-500 mt-0.5">Pick your busy groups and Brainydo turns them into quick highlight cards: what's being discussed, announcements, events, leads worth chasing, and anything that needs <b>you</b>.</p>
          </div>
          <button onClick={() => setManage(true)} className="h-10 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-[13px] font-semibold inline-flex items-center gap-1.5 active:scale-95 transition shrink-0">
            <Plus size={15} /> Pick groups
          </button>
        </div>
      ) : cards.length === 0 ? (
        <div className="px-6 py-8 text-center">
          <div className="text-[28px]">⏳</div>
          <div className="text-[14px] font-semibold text-slate-800 mt-1">Collecting chatter from {data.enabled_count} group{data.enabled_count === 1 ? "" : "s"}</div>
          <p className="text-[12.5px] text-slate-500">First highlights arrive within 2 hours — or <button onClick={refresh} disabled={refreshing} className="text-blue-600 font-semibold underline">build them now</button>.</p>
        </div>
      ) : (
        <div ref={scroller} className="flex gap-3 overflow-x-auto snap-x snap-mandatory p-4 [scrollbar-width:thin]">
          {cards.map((c, i) => <PulseCard key={c.chat_id} c={c} i={i} />)}
        </div>
      )}

      {manage && <ManageGroups onClose={() => { setManage(false); load(); }} />}
    </section>
  );
}
